/**
 * Aplikační jména pro typy z OpenAPI.
 *
 * Tvary NEJSOU psané ručně — všechny se odvozují z `generated.ts`, který vzniká
 * příkazem `npm run gen:api` proti `/api/openapi.json`. Tenhle soubor jen dává
 * vygenerovaným schématům čitelná jména a drží konstanty, které se do OpenAPI
 * nedostanou (jsou to hodnoty, ne typy).
 *
 * Když se změní backend: `npm run gen:api` → tsc ukáže, co v UI přestalo sedět.
 * Tím je kontrakt vynucený překladačem, ne dohodou.
 *
 * Pozn.: některá schémata mají v OpenAPI plně kvalifikované jméno
 * (`app__overview__schemas__ComparisonOut`), protože stejný název používá víc
 * modulů. Aliasy níže to schovávají.
 */
import type { components } from './generated';

type S = components['schemas'];

// ---------------------------------------------------------------------------
// Statistika a společné stavební kameny
// ---------------------------------------------------------------------------

/** Podíl s Wilsonovým intervalem. `rate` je nullable — nula a „nevíme" nejsou totéž. */
export type Proportion = S['ProportionOut'];

/** Dvouvýběrový z-test proporcí. Nese n, rozdíl, významnost i česká varování. */
export type Comparison = S['app__overview__schemas__ComparisonOut'];
export type GuardComparison = S['app__guard__schemas__ComparisonOut'];
export type ExperimentComparison = S['app__experiments__schemas__ComparisonOut'];

export type MetricValue = S['MetricValueOut'];
export type Period = S['PeriodOut'];
export type TabMeta = S['TabMetaOut'];
export type Meta = S['MetaOut'];
export type EmptyStateInfo = S['EmptyStateOut'];
export type Verdict = S['VerdictOut'];
export type PlacementInfo = S['PlacementInfoOut'];
export type Signal = S['SignalOut'];
export type SignalCode = Signal['code'];
export type Severity = Signal['severity'];

export type TimeseriesPoint = S['TimeseriesPointOut'];
export type SparkPoint = S['SparkPointOut'];
export type SegmentRow = S['app__overview__schemas__SegmentRowOut'];
export type GuardSegmentRow = S['app__guard__schemas__SegmentRowOut'];
export type HeatmapCell = S['app__overview__schemas__HeatmapCellOut'];
export type GuardHeatmapCell = S['app__guard__schemas__HeatmapCellOut'];
export type HourCell = S['HourCellOut'];
export type TtcBucket = S['TtcBucketOut'];

// ---------------------------------------------------------------------------
// Metriky a dotazy (01)
// ---------------------------------------------------------------------------

export type MetricDef = S['MetricOut'];
export type DimensionDef = S['DimensionOut'];
/**
 * Klíč metriky. Backend ho v OpenAPI posílá jako volný `string`, protože katalog
 * je datový (`/metrics/catalog`), ne výčtový — UI si ho tedy nesmí zafixovat
 * do unionu, jinak by přidání metriky na backendu rozbilo překlad.
 */
export type MetricKey = string;
export type DimensionKey = string;
export type MetricCatalog = S['CatalogOut'];
export type MetricsQueryRequest = S['MetricsQueryIn'];
export type MetricsQueryResponse = S['MetricsQueryOut'];
export type QueryFilter = S['FilterIn'];
export type MetricUnit = MetricValue['unit'];

// ---------------------------------------------------------------------------
// Auth a provoz
// ---------------------------------------------------------------------------

export type CurrentUser = S['UserOut'];
/** Uživatel ve správě je tentýž tvar jako přihlášený — hash hesla se nikdy nevrací. */
export type User = S['UserOut'];
export type Role = CurrentUser['role'];
export type LoginRequest = S['LoginIn'];

// --- správa uživatelů ------------------------------------------------------

export type UserListResponse = S['UserListOut'];
export type UserCreateRequest = S['UserCreateIn'];
export type UserUpdateRequest = S['UserUpdateIn'];
export type SetUserPasswordRequest = S['PasswordSetIn'];
export type ChangeOwnPasswordRequest = S['PasswordChangeIn'];

/**
 * Minimální délka hesla — musí sedět s validací na backendu (422).
 * Konstanta v OpenAPI není (je to hodnota, ne typ), takže žije tady.
 */
export const PASSWORD_MIN_LENGTH = 12;

export const ROLE_LABEL: Record<Role, string> = {
  viewer: 'Viewer — jen čte',
  analyst: 'Analyst — spouští joby, edituje poznatky',
  admin: 'Admin — plus správa a konfigurace AI',
};

export const ROLE_SHORT: Record<Role, string> = {
  viewer: 'viewer',
  analyst: 'analyst',
  admin: 'admin',
};

export const ROLES: readonly Role[] = ['viewer', 'analyst', 'admin'];
export type EtlHealth = S['EtlHealth'];
export type JobHealth = S['JobHealth'];
export type JobRun = S['app__etl__router__JobRunOut'];

// --- registr pozic (role admin) --------------------------------------------

/** Jedna pozice v registru: příznaky ingest/ab_test/guard + label a poznámka. */
export type PositionConfig = S['PositionConfigOut'];
/** Registr: seznam pozic, nabídka nedávno viděných identifikátorů a nápověda. */
export type PositionRegistry = S['PositionRegistryOut'];
export type PositionImportRequest = S['PositionImportIn'];
export type PositionPatchRequest = S['PositionPatchIn'];

// ---------------------------------------------------------------------------
// 02 — Overview
// ---------------------------------------------------------------------------

export type OverviewResponse = S['PositionsResponse'];
export type OverviewRow = S['PositionRowOut'];
export type OverviewTotals = S['TotalsOut'];

/** Položka katalogu placementů pro vyhledávací multiselect (02 FR-1). */
export type PlacementOption = S['PlacementOption'];
export type PlacementCatalog = S['PlacementCatalogResponse'];

export type PositionSummary = S['SummaryResponse'];
export type PositionVariants = S['VariantsResponse'];
export type PositionTime = S['TimeResponse'];
export type PositionGeo = S['GeoResponse'];
export type PositionDevice = S['DeviceResponse'];
export type DevicesOverview = S['DevicesOverviewResponse'];
export type SideSegment = S['SideSegmentOut'];
export type PositionGeometry = S['GeometryResponse'];
export type PositionEconomy = S['EconomyResponse'];
export type PositionChannels = S['ChannelsResponse'];
export type Compare = S['CompareResponse'];
export type ComparePlacement = S['ComparePlacementOut'];
export type ChannelSeries = S['ChannelSeriesOut'];
export type PositionStyles = S['StylesResponse'];
export type StyleMedia = S['StyleMediaOut'];
export type StyleTemplate = S['StyleTemplateOut'];
export type StyleMediaSeries = S['StyleMediaSeriesOut'];
export type StyleLoss = S['StyleLossOut'];
export type StyleLossKind = S['StyleLossKindOut'];
export type StyleLossStyle = S['StyleLossStyleOut'];
export type StyleLossValue = S['StyleLossValueOut'];
export type PositionImpOrder = S['ImpOrderResponse'];
export type ImpOrderProfile = S['ImpOrderProfileOut'];
export type ImpOrderRow = S['ImpOrderRowOut'];
export type ImpOrderRate = S['ImpOrderRateOut'];
export type ImpOrderPoint = S['ImpOrderPointOut'];
export type ImpOrderChannelRow = S['ImpOrderChannelRowOut'];
export type ImpOrderCoverage = S['ImpOrderCoverageOut'];
export type PositionQuality = S['QualityResponse'];
export type VariantRow = S['VariantRowOut'];

// ---------------------------------------------------------------------------
// 03 — Experimenty (BEZ Trella — zakládají se ručně)
// ---------------------------------------------------------------------------

export type Experiment = S['ExperimentDetailOut'];
export type ExperimentListItem = S['ExperimentListItem'];
export type ExperimentCreate = S['ExperimentCreate'];
export type DkSplitCreate = S['DkSplitCreate'];
export type PlacementChannels = S['PlacementChannelsOut'];
export type ExperimentVariant = S['VariantOut'];
export type VariantCreate = S['VariantCreate'];
export type ExperimentEvaluation = S['EvaluationOut'];
/** Environment deep-dive (pill33 zobecněný): CTR variant napříč prostředím. */
export type ExperimentEnvironment = S['EnvironmentOut'];
export type ExperimentDecision = S['DecisionOut'];
export type DecisionCreate = S['DecisionCreate'];
export type MappingReview = S['MappingReviewOut'];
export type MappingCandidate = S['MappingCandidateOut'];
export type ExperimentState = ExperimentListItem['state'];

// ---------------------------------------------------------------------------
// 04 — Guard
// ---------------------------------------------------------------------------

export type GuardOverview = S['GuardOverviewOut'];
export type GuardWebRow = S['GuardWebOut'];
// Guard redesign — přehled (seznam pozic) + detail per pozice
export type GuardPlacements = S['GuardPlacementsOut'];
export type GuardPlacementRow = S['GuardPlacementOut'];
export type GuardPlacementDetail = S['GuardPlacementDetailOut'];
export type GuardCloserShare = S['CloserShareOut'];
export type GuardChannelShare = S['ChannelShareOut'];
export type GuardChannelSeries = S['GuardChannelSeriesOut'];
export type GuardFunnel = S['GuardFunnelOut'];
export type GuardRegionVariants = S['GuardRegionVariantsOut'];
export type GuardCompare = S['GuardCompareOut'];
export type GuardChannelDefs = S['GuardChannelDefsOut'];
export type GuardChannelGroup = S['ChannelGroupOut'];
export type GuardChannelDef = S['ChannelDefOut'];
export type GuardCompareChannel = S['CompareChannelOut'];
export type GuardStyles = S['GuardStylesOut'];
export type GuardStyleRow = S['GuardStyleRowOut'];
export type GuardStyleSeries = S['GuardStyleSeriesOut'];
export type GuardImpOrder = S['GuardImpOrderOut'];
export type GuardImpOrderRow = S['GuardImpOrderRowOut'];
export type GuardImpOrderSeries = S['GuardImpOrderSeriesOut'];
export type GuardWebDetail = S['GuardWebDetailOut'];
export type GuardHeatmap = S['HeatmapOut'];
export type GuardHeatmapFilters = S['HeatmapFiltersOut'];
/** Overlay zóna nad heatmapou (zavírací roh, štítek „Reklama"). */
export type Overlay = S['OverlayOut'];
export type GuardOverlay = Overlay;
export type FraudAssessment = S['FraudAssessmentOut'];
export type GuardPhases = S['PhaseBreakdownListOut'];
export type GuardRiskReport = S['RiskReportOut'];
export type FraudLayer = S['FraudLayerOut'];
export type FraudFlag = S['FraudFlagOut'];
export type SspMix = S['SspMixOut'];
export type CloserConfig = S['CloserConfigOut'];
export type OursVsCompetition = S['OursVsCompetitionOut'];

// ---------------------------------------------------------------------------
// 11 — Mapa kliků
// ---------------------------------------------------------------------------

export type ClickCatalog = S['ClickCatalogResponse'];
export type ClickZoneDef = S['ZoneDefOut'];
export type ClickZoneRect = S['ZoneRectOut'];
export type ClickOverview = S['ClickMapOverviewResponse'];
export type ClickSliceStats = S['SliceStatsOut'];
export type ClickSlice = S['ClickSliceOut'];
export type ClickPlacement = S['ClickPlacementOut'];
export type ClickZoneStat = S['ClickZoneStatOut'];
export type ClickTtcSeries = S['TtcSeriesOut'];
export type ClickLockWindow = S['ClickLockWindowOut'];
export type ClickUncertainRow = S['UncertainRowOut'];
export type ClickUncertainSide = S['UncertainSideOut'];
export type ClickHeatmap = S['ClickHeatmapResponse'];
export type ClickSide = S['ClickSideOut'];
export type ClickCell = S['ClickCellOut'];
export type ClickZoneCompare = S['ZoneCompareOut'];
export type RateRatio = S['RateRatioOut'];
export type ScaledRate = S['ScaledRateOut'];

// ---------------------------------------------------------------------------
// 05 — Debt
// ---------------------------------------------------------------------------

export type DebtOverview = S['DebtOverview'];
export type DebtFamilyOverview = S['FamilyOverview'];
export type DebtFamilyDetail = S['FamilyDetail'];
export type DebtFamilyHealth = S['FamilyHealth'];
export type DebtFinancialPanel = S['FinancialPanel'];
export type DebtDayShapePoint = S['DayShapePoint'];
export type DebtMemberRow = S['MemberRow'];
export type DebtTimelineEvent = S['TimelineEvent'];

// ---------------------------------------------------------------------------
// 06 — Znalostní báze
// ---------------------------------------------------------------------------

export type Insight = S['InsightOut'];
export type InsightList = S['InsightListOut'];
/** Položka seznamu je tentýž tvar jako detail — backend nemá zvláštní list schéma. */
export type InsightListItem = S['InsightOut'];
export type InsightCreate = S['InsightCreate'];
/** Výsledek zápisu poznatku — nese příznak `created` a případné near-duplikáty. */
export type WriteResult = S['WriteResultOut'];
export type WikiNote = S['WikiNoteOut'];
export type KnowledgeSearchResult = S['SearchOut'];
export type KnowledgeStats = S['KnowledgeStatsOut'];

// ---------------------------------------------------------------------------
// 07 — AI vrstva
// ---------------------------------------------------------------------------

export type AiConfig = S['AiConfigOut'];
export type AiConfigAdmin = S['AiConfigAdminOut'];
export type AiConfigUpdate = S['AiConfigUpdate'];
export type AiRun = S['AiRunOut'];
export type AiRunList = S['AiRunListOut'];
export type AiRunDetail = S['AiRunDetailOut'];
export type AiJob = S['JobOut'];
export type AiBudget = S['BudgetOut'];
export type AiModelInfo = S['ModelInfo'];
export type YieldTip = S['TipOut'];
export type ChatSession = S['ChatSessionOut'];
export type ChatMessage = S['ChatMessageOut'];
export type ChatAsk = S['ChatAskIn'];

// ---------------------------------------------------------------------------
// Konstanty a doménové hodnoty
//
// Tohle NEJSOU typy, takže je OpenAPI nepřenáší — musí žít tady a musí sedět
// s backendem (`app/core/metrics.py`, `app/core/stats.py`).
// ---------------------------------------------------------------------------

/** Zavírací roh v per-mille souřadnicích kreativy (core/metrics.py). */
export const CORNER_X_MIN = 867;
export const CORNER_Y_MAX = 133;

/** Mřížka heatmapy — 50 ‰ na buňku = 20×20 (core/metrics.py HEATMAP_BUCKET_PERMILLE). */
export const HEATMAP_BUCKET_PERMILLE = 50;

/** Pixelové kohorty (`asPixels`) logují na ~300px kreativě. */
export const PX_TO_PERMILLE = 1000 / 300;
export const PX_DETECT_P99X = 330;

/** Prahy malého vzorku z core/stats.py. */
export const SMALL_SAMPLE_TRIALS = 1000;
export const SMALL_SAMPLE_SUCCESSES = 30;

export type Supplier = 'A' | 'B' | 'C' | 'other';
export type Phase = 'first_look' | 'refresh';

/**
 * Metriky odvozené z tržeb. Backend u nich posílá `available: false`
 * (MetricValueOut) — tenhle seznam je pojistka pro místa, kde přijde holé číslo.
 * Nula NENÍ výsledek: ve fixture prostředí je `price` = 0.
 */
export const REVENUE_DERIVED_METRICS: readonly string[] = [
  'revenue',
  'ecpm',
  'win_ecpm',
  'fill_rate',
  'win_rate',
];

// ---------------------------------------------------------------------------
// Filtry sdílené napříč stránkami (URL-serializovatelné, čistě klientské)
// ---------------------------------------------------------------------------

export type CompareMode = 'none' | 'prev_period' | 'prev_week';

export interface GlobalFilters {
  date_from: string;
  date_to: string;
  compare: CompareMode;
  domain: string[];
  type: string[];
  supplier: Supplier[];
  phase: Phase[];
  channel_tag: string[];
  /**
   * Konkrétní placementy (02 FR-1). Overview je filtruje jedním vyhledávacím
   * multiselectem místo čtyř dropdownů; backend je AND-uje. Prázdné = všechny.
   */
  placement_ids: string[];
}
